import React, { useState, useEffect } from 'react';
import { API_BASE } from '../apiBase';
import axios from 'axios';

const Settings: React.FC = () => {
    const [pineconeConfig, setPineconeConfig] = useState({
        apiKey: '',
        indexName: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [cleanupMessage, setCleanupMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Sync State
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Load Shopify App Bridge script dynamically
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://cdn.shopify.com/shopifycloud/app-bridge.js';
        script.async = true;
        script.onload = () => console.log('✅ Shopify App Bridge script loaded');
        script.onerror = () => console.error('❌ Failed to load Shopify App Bridge script');
        document.head.appendChild(script);
        return () => {
            document.head.removeChild(script);
        };
    }, []);

    useEffect(() => {
        // Load existing settings from local storage or backend
        // For now, we'll pull from localStorage as per the onboarding flow
        const savedConfig = localStorage.getItem('pinecone_config');
        if (savedConfig) {
            try {
                setPineconeConfig(JSON.parse(savedConfig));
            } catch (e) {
                console.error('Failed to parse saved config');
            }
        }
    }, []);

    const handleSave = async () => {
        setLoading(true);
        setMessage(null);
        try {
            // Test the connection first
            const response = await axios.post(`${API_BASE}/api/pinecone/test-connection`, pineconeConfig, { withCredentials: true });

            if (response.data.success) {
                // Save to local storage (and ideally backend user profile in the future)
                localStorage.setItem('pinecone_config', JSON.stringify(pineconeConfig));
                setMessage({ type: 'success', text: 'Settings saved and connection verified!' });
            } else {
                setMessage({ type: 'error', text: 'Connection failed: ' + response.data.error });
            }
        } catch (error: any) {
            setMessage({ type: 'error', text: 'Error saving settings: ' + (error.response?.data?.error || error.message) });
        } finally {
            setLoading(false);
        }
    };

    const handleCleanupCredentials = async () => {
        setCleanupLoading(true);
        setCleanupMessage(null);
        try {
            const response = await axios.post(
                `${API_BASE}/api/cleanup-invalid-shopify-credentials`,
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setCleanupMessage({
                    type: 'success',
                    text: response.data.message + (response.data.shops?.length > 0 ? ` (${response.data.shops.join(', ')})` : '')
                });
                // Reload the page after 2 seconds to refresh Shopify status
                setTimeout(() => window.location.reload(), 2000);
            } else {
                setCleanupMessage({ type: 'error', text: 'Cleanup failed: ' + response.data.error });
            }
        } catch (error: any) {
            setCleanupMessage({
                type: 'error',
                text: 'Error cleaning credentials: ' + (error.response?.data?.error || error.message)
            });
        } finally {
            setCleanupLoading(false);
        }
    };

    const handleSync = async (clearFirst = false) => {
        const confirmMsg = clearFirst
            ? 'This will WIPE your entire Pinecone index and then sync ONLY ACTIVE products from Shopify. This is recommended if your database contains drafts or incorrect data. Continue?'
            : 'This will fetch all ACTIVE products from Shopify and upload them to Pinecone. Existing items will be updated. Continue?';

        if (!window.confirm(confirmMsg)) {
            return;
        }

        setSyncLoading(true);
        setSyncMessage({ type: 'success', text: (clearFirst ? 'Clearing and ' : '') + 'Sync started... This can take 5-10 minutes. Please do not close this tab.' });

        try {
            const response = await axios.post(
                `${API_BASE}/api/products/sync-pinecone`,
                { clearFirst },
                { withCredentials: true, timeout: 600000 } // 10 min timeout
            );

            if (response.data.success) {
                const { processedProducts, successfulVectors, errors } = response.data.data;
                setSyncMessage({
                    type: 'success',
                    text: `✅ ${clearFirst ? 'Clean ' : ''}Sync Complete! Processed ${processedProducts} products (${successfulVectors} variants). Errors: ${errors}`
                });
            } else {
                setSyncMessage({ type: 'error', text: 'Sync failed: ' + response.data.error });
            }
        } catch (error: any) {
            console.error('Sync error:', error);
            setSyncMessage({
                type: 'error',
                text: 'Error syncing: ' + (error.response?.data?.error || error.message || 'Timeout or Network Error.')
            });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleClearIndex = async () => {
        if (!window.confirm('⚠️ WARNING: This will PERMANENTLY DELETE all product vectors from your Pinecone index. This cannot be undone. Are you absolutely sure?')) {
            return;
        }

        setSyncLoading(true);
        setSyncMessage({ type: 'success', text: 'Clearing index... please wait.' });

        try {
            const response = await axios.post(
                `${API_BASE}/api/products/sync-pinecone`,
                { action: 'clear' },
                { withCredentials: true }
            );

            if (response.data.success) {
                setSyncMessage({ type: 'success', text: '✅ Database cleared successfully! Your index is now empty.' });
            } else {
                setSyncMessage({ type: 'error', text: 'Clear failed: ' + response.data.error });
            }
        } catch (error: any) {
            setSyncMessage({
                type: 'error',
                text: 'Error clearing: ' + (error.response?.data?.error || error.message)
            });
        } finally {
            setSyncLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-8">
            <h1 className="text-3xl font-bold mb-8 text-slate-800">App Settings</h1>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <span className="text-2xl mr-2">🌲</span> Pinecone Configuration
                </h2>
                <p className="text-slate-600 mb-6">
                    Manage your connection to the Pinecone vector database for product recommendations.
                </p>

                <div className="space-y-4 max-w-lg">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
                        <input
                            type="password"
                            value={pineconeConfig.apiKey}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, apiKey: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"
                            placeholder="pcsk_..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Index Name</label>
                        <input
                            type="text"
                            value={pineconeConfig.indexName}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, indexName: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"
                        />
                    </div>

                    {message && (
                        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                            {message.text}
                        </div>
                    )}

                    <div className="pt-4">
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="px-6 py-2 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Verifying & Saving...' : 'Save Configuration'}
                        </button>
                    </div>

                    {/* Sync Section */}
                    <div className="mt-8 pt-6 border-t border-slate-200">
                        <h3 className="text-lg font-medium mb-2">Product Database Synchronization</h3>
                        <p className="text-sm text-slate-600 mb-4">
                            Upload all your <strong>ACTIVE</strong> Shopify products (variants, metafields, descriptions) to the vector database.
                            Draft and archived products are excluded.
                        </p>

                        {syncMessage && (
                            <div className={`p-4 rounded-lg mb-4 text-sm ${syncMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {syncMessage.text}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-4">
                            <button
                                onClick={() => handleSync(false)}
                                disabled={syncLoading}
                                className="px-6 py-2 bg-fuchsia-600 text-white rounded-lg font-semibold hover:bg-fuchsia-700 transition-colors disabled:opacity-50 flex items-center"
                            >
                                {syncLoading ? <span className="animate-spin mr-2">🔄</span> : <span className="mr-2">🚀</span>}
                                Sync Active Products
                            </button>

                            <button
                                onClick={() => handleSync(true)}
                                disabled={syncLoading}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center"
                            >
                                {syncLoading ? <span className="animate-spin mr-2">🔄</span> : <span className="mr-2">✨</span>}
                                Clean Re-sync (Active Only)
                            </button>

                            <button
                                onClick={handleClearIndex}
                                disabled={syncLoading}
                                className="px-6 py-2 bg-red-100 text-red-700 border border-red-200 rounded-lg font-semibold hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center"
                            >
                                <span className="mr-2">🗑️</span> Emergency Clear Database
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <span className="text-2xl mr-2">🛍️</span> Shopify Connection
                </h2>
                <p className="text-slate-600 mb-4">
                    If you're experiencing issues with your Shopify connection, use this to clean up invalid credentials.
                </p>

                <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg text-sm mb-4">
                    ⚠️ <strong>When to use this:</strong> If you see "Decryption failed" errors in the logs or your Shopify connection shows as connected but isn't working.
                </div>

                {cleanupMessage && (
                    <div className={`p-4 rounded-lg mb-4 ${cleanupMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        {cleanupMessage.text}
                        {cleanupMessage.type === 'success' && <div className="mt-2 text-sm">Reloading in 2 seconds...</div>}
                    </div>
                )}

                <button
                    onClick={handleCleanupCredentials}
                    disabled={cleanupLoading}
                    className="px-6 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                    {cleanupLoading ? 'Cleaning...' : 'Clear Invalid Credentials'}
                </button>

                <p className="text-xs text-slate-500 mt-3">
                    After clearing, you'll need to reconnect your Shopify store from the Account page.
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <span className="text-2xl mr-2">🤖</span> AI Model Configuration
                </h2>
                <p className="text-slate-600 mb-4">
                    The app uses <strong>Google Gemini</strong> for content generation.
                </p>
                <div className="bg-blue-50 text-blue-800 p-4 rounded-lg text-sm">
                    ℹ️ The AI API keys are managed by the app administrator. You do not need to configure them here.
                </div>
            </div>
        </div>
    );
};

export default Settings;
